import React, { createContext, useContext, useState, useCallback } from "react";

export const ViewerContext = createContext();

export const ViewerProvider = ({ children }) => {
  // --- Annotation state ---
  const [annotationsData, setAnnotationsData] = useState({});
  const [selectedAnnotationId, setSelectedAnnotationId] = useState(null);
  const [parsedDicomData, setParsedDicomData] = useState(null);

  const [viewportRefs, setViewportRefs] = useState(null);
  const [meta, setContextMeta] = useState(null);

  const addAnnotationToData = useCallback((annotation, sliceIndex) => {
    setAnnotationsData((prev) => {
      const copy = { ...prev };
      if (!copy[sliceIndex]) copy[sliceIndex] = [];
      copy[sliceIndex].push(annotation);
      return copy;
    });
  }, []);

  const deleteAnnotationFromData = useCallback((annotationId) => {
    setAnnotationsData((prev) => {
      const updated = {};
      for (const slice in prev) {
        updated[slice] = prev[slice].filter((a) => a.id !== annotationId);
      }
      return updated;
    });
  }, []);

  const upsertAnnotationInData = useCallback((annotation, sliceIndex) => {
    setAnnotationsData((prev) => {
      const copy = { ...prev };
      const list = copy[sliceIndex] ? [...copy[sliceIndex]] : [];
      const idx = list.findIndex((a) => a.id === annotation.id);
      if (idx === -1) {
        list.push(annotation);
      } else {
        list[idx] = { ...list[idx], ...annotation };
      }
      copy[sliceIndex] = list;
      return copy;
    });
  }, []);

  // --- Viewer-wide state ---
  const [activeTool, setActiveTool] = useState("MouseIcon");
  const [lastPrimaryTool, setLastPrimaryTool] = useState("MouseIcon");
  const [zoomTarget, setZoomTarget] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [viewPort, setViewPort] = useState("default");

  const [notesOpen, setNotesOpen] = useState(false);
  const [graphOpen, setGraphOpen] = useState(false);
  const [splitState, setSplitState] = useState("off");
  const [scrollLinked, setScrollLinked] = useState(false);

  // single entry-point for the header button
  const requestSplitToggle = useCallback(() => {
    setSplitState((prev) => {
      if (prev === "off") return "loading";
      if (prev === "on") return "off";
      return "off";
    });
  }, []);

  const zoomToAnnotation = (annotation) => {
    if (!annotation) {
      setZoom(1);
      setZoomTarget(null);
      return;
    }
    if (annotation.type === "personal") return;

    // Handle measurements with start/end coordinates
    const { start, end } = annotation;
    if (
      start?.x != null &&
      start?.y != null &&
      end?.x != null &&
      end?.y != null
    ) {
      // Calculate midpoint for measurements
      const midpointX = (start.x + end.x) / 2;
      const midpointY = (start.y + end.y) / 2;

      const viewport = viewportRefs?.leftViewportRef?.current;
      if (viewport && meta) {
        const { imagePositionPatient } = meta;
        setZoom(1.5);
        setZoomTarget([midpointX, midpointY, imagePositionPatient[2]]);
      }
      return;
    }

    // Handle findings with x/y coordinates
    if (annotation.x != null && annotation.y != null) {
      const viewport = viewportRefs?.leftViewportRef?.current;
      if (viewport && meta) {
        const { imagePositionPatient } = meta;
        setZoom(1.5);
        setZoomTarget([annotation.x, annotation.y, imagePositionPatient[2]]);
      }
    }
  };

  // --- for creating off-screan snapshots ---
  const [imageIdCTX, setImageIdsCTX] = useState(null);
  const [examData, setExamData] = useState(null);

  return (
    <ViewerContext.Provider
      value={{
        // Annotation state
        annotationsData,
        setAnnotationsData,
        addAnnotationToData,
        deleteAnnotationFromData,
        selectedAnnotationId,
        setSelectedAnnotationId,
        upsertAnnotationInData,

        // Viewer state
        activeTool,
        setActiveTool,
        lastPrimaryTool,
        setLastPrimaryTool,
        zoom,
        setZoom,
        zoomTarget,
        setZoomTarget,
        zoomToAnnotation,
        viewPort,
        setViewPort,
        notesOpen,
        setNotesOpen,
        setViewportRefs,
        setContextMeta,

        //
        parsedDicomData,
        setParsedDicomData,
        imageIdCTX,
        setImageIdsCTX,
        splitState,
        setSplitState,
        requestSplitToggle,
        examData,
        setExamData,
        viewportRefs,
        meta,

        //
        graphOpen,
        setGraphOpen,
        scrollLinked,
        setScrollLinked,
      }}>
      {children}
    </ViewerContext.Provider>
  );
};

export const useViewer = () => useContext(ViewerContext);
